// GtLexaAdmin: the admin Lexa screen, under Interviews (/interviews/lexa).
//
// The model Lexa thinks with (Claude Haiku 5.5 or GPT-6.1 Sol), Try Lexa for a
// test call on either model without using a credit, this month's numbers,
// every session with its recording, report and transcript, and payments.
// Admins and super admins only: the server refuses everyone else.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../../config';
import GtLexaSession, { TOPIC_LABEL, MODEL_LABEL, ukTime, ukDate, mins, statusOf, downloadPdf } from '../../components/GtLexaSession';

export function useAuthFetch(getToken) {
  return useCallback(async (p, init) => {
    const t = await getToken();
    const r = await fetch(API_BASE + p, { ...(init || {}), headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t, ...((init && init.headers) || {}) } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.message || j.error || 'Something went wrong.');
    return j;
  }, [getToken]);
}
export function initials(name) { return (name || '?').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
export function money(currency, minor) {
  const sym = { gbp: '£', usd: 'US$', eur: '€' }[currency] || '';
  const v = (Number(minor) || 0) / 100;
  return sym + (Number.isInteger(v) ? v : v.toFixed(2));
}
const PAY = { paid: 'Paid', pending: 'Not finished', expired: 'Abandoned', refunded: 'Refunded', part_refunded: 'Part refunded' };

// Nathan, Lexa and Beth share the Interviews item in the menu.
export function LexaSwitch({ active }) {
  const navigate = useNavigate();
  return (
    <div className="gtlx-switch" role="tablist" aria-label="Interviews">
      <button type="button" role="tab" aria-selected={active === 'nathan'} className={'gtlx-sw' + (active === 'nathan' ? ' gtlx-sw--on' : '')} onClick={() => navigate('/interviews')}>
        <span className="gtiv-orb gtlx-sw-orb" aria-hidden="true" />Nathan<em>interviews for roles</em>
      </button>
      <button type="button" role="tab" aria-selected={active === 'lexa'} className={'gtlx-sw' + (active === 'lexa' ? ' gtlx-sw--on' : '')} onClick={() => navigate('/interviews/lexa')}>
        <span className="gtlx-orb gtlx-sw-orb" aria-hidden="true" />Lexa<em>coaching for candidates</em>
      </button>
      {/* C1-BETH8: Beth's website conversations sit beside Nathan and Lexa. */}
      <button type="button" role="tab" aria-selected={active === 'beth'} className={'gtlx-sw' + (active === 'beth' ? ' gtlx-sw--on' : '')} onClick={() => navigate('/interviews/beth')}>
        <span className="gtbt-orb gtlx-sw-orb" aria-hidden="true" />Beth<em>conversations on the website</em>
      </button>
    </div>
  );
}

function Chips({ value, options, onChange, label }) {
  return (
    <div className="gtlx-chips" role="radiogroup" aria-label={label}>
      {options.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} className={'gtlx-chip' + (value === k ? ' gtlx-chip--on' : '')} onClick={() => onChange(k)}>{l}</button>
      ))}
    </div>
  );
}

function TryLexa({ authFetch, getToken, settings, status, flash, onStarted }) {
  const [kind, setKind] = useState('interview');
  const [mode, setMode] = useState('practice');
  const [roles, setRoles] = useState([]);
  const [roleQ, setRoleQ] = useState('');
  const [roleRef, setRoleRef] = useState('');
  const [usePaste, setUsePaste] = useState(false);
  const [jd, setJd] = useState('');
  const [topic, setTopic] = useState('next_move');
  const [goal, setGoal] = useState('');
  const [model, setModel] = useState('setting');
  const [asQ, setAsQ] = useState('');
  const [asList, setAsList] = useState([]);
  const [asWho, setAsWho] = useState(null);
  const [background, setBackground] = useState('');
  const [cvName, setCvName] = useState('');
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    fetch(API_BASE + '/api/public/roles').then((r) => r.json()).then((j) => setRoles(Array.isArray(j) ? j : (j.roles || []))).catch(() => setRoles([]));
  }, []);
  useEffect(() => {
    if (asWho || asQ.trim().length < 2) { setAsList([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      authFetch('/api/lexa/candidates?q=' + encodeURIComponent(asQ.trim())).then((r) => { if (live) setAsList(r.candidates || []); }).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [asQ, asWho, authFetch]);
  const shown = useMemo(() => roles.filter((r) => !roleQ || String(r.title || '').toLowerCase().indexOf(roleQ.toLowerCase()) !== -1).slice(0, 6), [roles, roleQ]);
  const picked = roles.find((r) => r.ref === roleRef);
  const ready = kind === 'career' ? true : usePaste ? jd.trim().length >= 80 : !!roleRef;

  async function readCv(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setReading(true); setError('');
    try {
      const t = await getToken();
      const r = await fetch(API_BASE + '/api/lexa/cv-text?name=' + encodeURIComponent(f.name), { method: 'POST', headers: { 'Content-Type': 'application/octet-stream', Authorization: 'Bearer ' + t }, body: f });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.message || 'Could not read that file.');
      setBackground(j.text); setCvName(f.name);
    } catch (err2) { setError(err2.message); } finally { setReading(false); }
  }

  const need = kind === 'interview' && !ready
    ? (usePaste ? 'Paste the job description (a few lines at least) to start.' : 'Choose a role above, or paste a job description, to start a mock interview.')
    : '';
  async function start() {
    if (need) { setError(need); return; }
    const w = window.open('', '_blank');
    setBusy(true); setError('');
    try {
      const body = { kind, goal: goal.trim() || undefined, model: model === 'setting' ? undefined : model, candidate_id: asWho ? asWho.id : undefined, background: background.trim() || undefined };
      if (kind === 'interview') { body.mode = mode; if (usePaste) body.job_description = jd; else body.role_ref = roleRef; } else body.topic = topic;
      const r = await authFetch('/api/lexa/test', { method: 'POST', body: JSON.stringify(body) });
      const url = window.location.origin + '/lexa/' + r.token;
      if (w) w.location.href = url; else window.location.href = url;
      flash('Test call opened in a new tab');
      onStarted();
    } catch (e) { if (w) w.close(); setError(e.message); } finally { setBusy(false); }
  }

  return (
    <section className="gtiv-card gtlx-try">
      <div className="gtlx-card-h">
        <span className="gtlx-orb" aria-hidden="true" />
        <div><b>Try Lexa</b><span>A test call for you, on either model. Uses no credit and touches no candidate's profile.</span></div>
      </div>
      <Chips label="Kind of session" value={kind} onChange={setKind} options={[['interview', 'Interview practice'], ['career', 'Career coaching']]} />
      {kind === 'interview' ? (
        <>
          <Chips label="Mode" value={mode} onChange={setMode} options={[['practice', 'Practice mode'], ['real', 'Real mode']]} />
          {!usePaste ? (
            <div className="gtlx-rolepick">
              {picked ? (
                <div className="gtlx-picked"><b>{picked.title}</b><button type="button" className="gtiv-link" onClick={() => setRoleRef('')}>Change</button></div>
              ) : (
                <>
                  <input className="gtiv-in" placeholder={roles.length ? 'Search the live roles' : 'No live roles: paste a job description instead'} value={roleQ} onChange={(e) => setRoleQ(e.target.value)} aria-label="Search roles" />
                  <div className="gtlx-rolelist">
                    {shown.map((r) => <button key={r.ref} type="button" className="gtlx-role" onClick={() => setRoleRef(r.ref)}>{r.title}</button>)}
                  </div>
                </>
              )}
              <button type="button" className="gtiv-link" onClick={() => setUsePaste(true)}>Paste a job description instead</button>
            </div>
          ) : (
            <div className="gtlx-rolepick">
              <textarea className="gtiv-in" rows={4} placeholder="Paste the job description" value={jd} onChange={(e) => setJd(e.target.value)} aria-label="Job description" />
              <button type="button" className="gtiv-link" onClick={() => setUsePaste(false)}>Choose a live role instead</button>
            </div>
          )}
        </>
      ) : (
        <Chips label="Topic" value={topic} onChange={setTopic} options={Object.entries(TOPIC_LABEL)} />
      )}
      <div className="gtlx-as">
        <span className="gtlx-as-k">Who is Lexa coaching?</span>
        <textarea className="gtiv-in gtlx-cvbox" rows={5} value={background} onChange={(e) => setBackground(e.target.value)}
          placeholder="Paste a CV, or the text of a LinkedIn profile (select everything on the profile page, copy, paste here)" aria-label="CV or LinkedIn profile" />
        <div className="gtlx-as-row">
          <label className={'gtc-btn-ghost gtlx-upload' + (reading ? ' gtlx-upload--busy' : '')}>
            <input type="file" accept=".pdf,.doc,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={readCv} disabled={reading} />
            {reading ? 'Reading the CV…' : 'Upload a CV (PDF or Word)'}
          </label>
          {cvName && <span className="gtlx-as-n">From {cvName}</span>}
          {background && <button type="button" className="gtiv-link" onClick={() => { setBackground(''); setCvName(''); }}>Clear</button>}
        </div>
        <span className="gtlx-as-k gtlx-as-k--or">Or use a candidate already on file</span>
        {asWho ? (
          <div className="gtlx-picked"><b>{asWho.full_name}</b><span>{[asWho.current_title, asWho.current_company].filter(Boolean).join(' at ') || asWho.ref}{asWho.has_cv ? '' : ' · no CV on file'}</span><button type="button" className="gtiv-link" onClick={() => { setAsWho(null); setAsQ(''); }}>Change</button></div>
        ) : (
          <>
            <input className="gtiv-in" placeholder="Search by name, email or reference" value={asQ} onChange={(e) => setAsQ(e.target.value)} aria-label="Candidate to test as" />
            {asList.length > 0 && (
              <div className="gtlx-rolelist">
                {asList.map((c) => (
                  <button key={c.id} type="button" className="gtlx-role" onClick={() => setAsWho(c)}>
                    <b>{c.full_name}</b>{c.current_title ? ' · ' + c.current_title : ''}{c.has_cv ? '' : ' · no CV'}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <span className="gtlx-as-n">{background || asWho ? 'Lexa reads this before the call and coaches you as that person. Still a test: no credit used, nothing saved to anyone\'s profile.' : 'Leave all of this empty and Lexa coaches from what you tell her.'}</span>
      </div>
      <input className="gtiv-in" placeholder={kind === 'career' ? 'What do you want from the session? (optional)' : 'Anything to work on? (optional)'} value={goal} onChange={(e) => setGoal(e.target.value)} aria-label="Goal" />
      <div className="gtlx-try-f">
        <Chips label="Model for this test" value={model} onChange={setModel}
          options={[['setting', 'As set (' + (MODEL_LABEL[settings && settings.model] || 'Claude Haiku 5.5') + ')'], ['haiku', 'Claude Haiku 5.5'], ['sol', 'GPT-6.1 Sol']]} />
        <span className="gtlx-go">
          {need && <span className="gtlx-need">{need}</span>}
          <button type="button" className="gtc-primary" disabled={busy || (status && !status.live)} onClick={start}>{busy ? 'Opening…' : 'Start a test call'}</button>
        </span>
      </div>
      {model === 'sol' && status && !status.sol_ready && <p className="gtiv-note gtiv-note--amber">OPENROUTER_API_KEY is not set on this server, so Sol would fall back to Claude Haiku 5.5.</p>}
      {status && !status.live && <p className="gtiv-note gtiv-note--amber">Live calls are off on this server. Missing settings: {status.missing.join(', ')}.</p>}
      {error && <p className="gtivl-err">{error}</p>}
    </section>
  );
}

export function SessionModal({ id, authFetch, getToken, onClose, onChanged, flash }) {
  const [d, setD] = useState(null);
  const [memory, setMemory] = useState(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const x = await authFetch('/api/lexa/sessions/' + id);
      setD(x);
      if (x.candidate_id) { const m = await authFetch('/api/lexa/memory/' + x.candidate_id); setMemory(m.memory || null); }
    } catch (e) { setError(e.message); }
  }, [authFetch, id]);
  useEffect(() => { load(); }, [load]);
  const waiting = d && d.status === 'completed' && !d.report && d.report_status !== 'failed';
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [waiting, load]);
  async function regenerate() {
    setBusy(true);
    try { setD(await authFetch('/api/lexa/sessions/' + id + '/report', { method: 'POST', body: '{}' })); flash('Report written again'); onChanged(); } catch (e) { flash(e.message); } finally { setBusy(false); }
  }
  const [pdfBusy, setPdfBusy] = useState(false);
  async function pdf() {
    setPdfBusy(true);
    try { await downloadPdf(getToken, '/api/lexa/sessions/' + id + '/pdf'); } catch (e) { flash(e.message); } finally { setPdfBusy(false); }
  }
  const st = d ? statusOf(d) : null;
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtlx-modal" onClick={(e) => e.stopPropagation()}>
        {!d ? (
          <div className="gtc-modal-body">{error ? <p className="gtivl-err">{error}</p> : <p className="gtivl-muted">Loading…</p>}</div>
        ) : (
          <>
            <div className="gtlx-modal-h">
              <span className={'gtivl-av' + (d.is_test ? ' gtlx-av--test' : '')}>{d.is_test ? 'T' : initials(d.full_name)}</span>
              <div className="gtlx-modal-t">
                <b>{d.is_test ? 'Test run by ' + (d.started_by_name || 'staff') + (d.full_name ? ' as ' + d.full_name : '') : d.full_name}</b>
                <span>{d.title} · {ukTime(d.started_at || d.created_at)}</span>
              </div>
              <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
              {d.report && !d.report.short && <button type="button" className="gtc-btn-ghost gtlx-pdfbtn" disabled={pdfBusy} onClick={pdf}>{pdfBusy ? 'Preparing…' : 'Download PDF'}</button>}
              <button type="button" className="gtivl-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <GtLexaSession d={d} admin memory={d.candidate_id ? memory : undefined} busy={busy} onRegenerate={regenerate}
              youLabel={d.is_test ? 'Tester' : (d.full_name || 'Candidate').split(' ')[0]} />
          </>
        )}
      </div>
    </div>
  );
}

export default function GtLexaAdmin({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const [toast, setToast] = useState(null);
  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(null), 2800); }, []);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [totals, setTotals] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('real');
  const [kind, setKind] = useState('');
  const [model, setModel] = useState('');
  const [viewId, setViewId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const loadSessions = useCallback(async () => {
    const qs = '?filter=' + filter + (kind ? '&kind=' + kind : '') + (model ? '&model=' + model : '');
    try { setSessions((await authFetch('/api/lexa/sessions' + qs)).sessions); setError(''); } catch (e) { setError(e.message); }
  }, [authFetch, filter, kind, model]);
  const loadRest = useCallback(async () => {
    try {
      const [s, st, t, p] = await Promise.all([authFetch('/api/lexa/settings'), authFetch('/api/lexa/status'), authFetch('/api/lexa/totals'), authFetch('/api/lexa/payments')]);
      setSettings(s); setStatus(st); setTotals(t); setPayments(p.payments);
    } catch (e) { setError(e.message); }
  }, [authFetch]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadRest(); }, [loadRest]);
  const writing = !!(sessions && sessions.some((s) => s.status === 'in_progress' || (s.status === 'completed' && s.report_status !== 'done' && s.report_status !== 'failed')));
  useEffect(() => {
    if (!writing) return undefined;
    const t = setInterval(loadSessions, 15000);
    return () => clearInterval(t);
  }, [writing, loadSessions]);

  async function pickModel(m) {
    if (!settings || settings.model === m) return;
    setSaving(true);
    try { const s = await authFetch('/api/lexa/settings', { method: 'PUT', body: JSON.stringify({ model: m }) }); setSettings((o) => ({ ...o, ...s })); flash('Lexa now thinks with ' + MODEL_LABEL[m]); } catch (e) { flash(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="gtc-wrap gtivs gtlx-admin">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Lexa</h1>
          <p className="gtc-sub">Candidates' own coaching sessions. Never shown or sent to clients.</p>
        </div>
      </div>
      <LexaSwitch active="lexa" />
      {error && <div className="gtiv-card gtiv-err">{error} <button type="button" className="gtiv-link" onClick={() => { loadRest(); loadSessions(); }}>Try again</button></div>}

      <div className="gtlx-top">
        <section className="gtiv-card gtlx-model">
          <div className="gtlx-card-h">
            <span className="gtlx-orb" aria-hidden="true" />
            <div><b>How Lexa thinks</b><span>Every new session uses this. Each session records which model ran it.</span></div>
          </div>
          <div className="gtlx-models">
            {((settings && settings.models) || [{ key: 'haiku', label: 'Claude Haiku 5.5' }, { key: 'sol', label: 'GPT-6.1 Sol' }]).map((m) => (
              <button key={m.key} type="button" className={'gtiv-modeopt' + (settings && settings.model === m.key ? ' gtiv-modeopt--on' : '')} disabled={!settings || saving} onClick={() => pickModel(m.key)} aria-pressed={!!settings && settings.model === m.key}>
                <b>{m.label}</b>
                <span>{m.key === 'haiku' ? 'About 4p to 6p a session. Direct Anthropic connection.' : 'About 55p to 90p a session, through OpenRouter. Falls back to Claude Haiku 5.5 if it fails.'}</span>
              </button>
            ))}
          </div>
          {settings && settings.updated_by_name && <p className="gtiv-note">Last changed by {settings.updated_by_name}, {ukTime(settings.updated_at)}.</p>}
        </section>

        <section className="gtiv-card gtlx-month">
          <div className="gtlx-card-h"><div><b>This month</b><span>Real sessions only. Tests are counted apart.</span></div></div>
          <div className="gtlx-stats">
            <div><b>{totals ? totals.sessions_month : '–'}</b><span>sessions</span></div>
            <div><b>{totals ? totals.interview_month : '–'}</b><span>mock interviews</span></div>
            <div><b>{totals ? totals.career_month : '–'}</b><span>career sessions</span></div>
            <div><b>{totals ? totals.free_month : '–'}</b><span>free</span></div>
            <div><b>{totals ? totals.paid_month : '–'}</b><span>paid</span></div>
            <div><b>{totals ? totals.cost_month : '–'}</b><span>model cost</span></div>
          </div>
          <div className="gtlx-rev">
            {totals && totals.revenue.length ? totals.revenue.map((x) => (
              <span key={x.currency}><b>{x.month}</b> this month · {x.all} in all</span>
            )) : <span className="gtivl-muted">No payments yet.</span>}
            {totals && totals.tests_month > 0 && <span className="gtivl-muted">{totals.tests_month} test {totals.tests_month === 1 ? 'call' : 'calls'}</span>}
          </div>
          {settings && (
            <p className={'gtiv-note' + (settings.stripe.ready && settings.stripe.webhook_ready ? '' : ' gtiv-note--amber')}>
              {!settings.stripe.ready ? 'Stripe is off: add STRIPE_SECRET_KEY on the server.' : settings.stripe.test_mode ? 'Stripe is in test mode.' : 'Stripe is live.'}
              {settings.stripe.ready && !settings.stripe.webhook_ready ? ' Add STRIPE_WEBHOOK_SECRET too.' : ''}
              {' '}Webhook address: <code className="gtlx-code">{settings.stripe.webhook_url}</code>
            </p>
          )}
        </section>
      </div>

      <TryLexa authFetch={authFetch} getToken={getToken} settings={settings} status={status} flash={flash} onStarted={() => { setFilter('tests'); loadSessions(); }} />

      <div className="gtlx-listbar">
        <div className="gtivl-filters" role="tablist">
          {[['real', 'Candidates'], ['tests', 'Test calls'], ['all', 'All']].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={'gtivl-filter' + (filter === k ? ' gtivl-filter--on' : '')} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <Chips label="Kind" value={kind} onChange={setKind} options={[['', 'Both kinds'], ['interview', 'Mock interviews'], ['career', 'Career coaching']]} />
        <Chips label="Model" value={model} onChange={setModel} options={[['', 'Both models'], ['haiku', 'Haiku'], ['sol', 'Sol']]} />
      </div>

      {sessions === null ? <div className="gtiv-card gtiv-loading">Loading sessions…</div> : sessions.length === 0 ? (
        <div className="gtiv-card gtivl-empty">
          <span className="gtlx-orb gtlx-orb--lg" aria-hidden="true" />
          <b>{filter === 'tests' ? 'No test calls yet' : 'No sessions yet'}</b>
          <span>{filter === 'tests' ? 'Start one above to hear Lexa on either model.' : 'Candidates start sessions from the Lexa tab in their portal.'}</span>
        </div>
      ) : (
        <div className="gtivl-list">
          {sessions.map((s) => {
            const st = statusOf(s);
            return (
              <button key={s.id} type="button" className="gtiv-card gtivl-row gtlx-row" onClick={() => setViewId(s.id)}>
                <span className={'gtivl-av' + (s.is_test ? ' gtlx-av--test' : '')}>{s.is_test ? 'T' : initials(s.full_name)}</span>
                <span className="gtivl-row-t">
                  <b>{s.is_test ? 'Test run by ' + (s.started_by_name || 'staff') + (s.full_name ? ' as ' + s.full_name : '') : s.full_name}<em className="gtivl-for"> · {s.title}</em></b>
                  <span>
                    {ukTime(s.started_at || s.created_at)}
                    {s.duration_seconds ? ' · ' + mins(s.duration_seconds) : ''}
                    {s.kind === 'interview' ? ' · ' + (s.mode === 'real' ? 'Real mode' : 'Practice mode') : ''}
                    {s.cost_pence != null ? ' · ' + Number(s.cost_pence).toFixed(1) + 'p' : ''}
                  </span>
                </span>
                <span className={'gtlx-mtag gtlx-mtag--' + s.model}>{s.model === 'sol' ? 'Sol' : 'Haiku'}{s.model_ran && s.model_ran.fell_back ? ' → Haiku' : ''}</span>
                {s.recordings > 0 && <span className="gtivl-vid"><svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>Video</span>}
                <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <section className="gtiv-card gtlx-pay">
        <button type="button" className="gtlx-pay-h" onClick={() => setShowPay(!showPay)} aria-expanded={showPay}>
          <b>Payments</b><span>{payments ? payments.length + (payments.length === 1 ? ' payment' : ' payments') : ''}</span>
          <svg className={'gtlx-chev' + (showPay ? ' gtlx-chev--open' : '')} viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        {showPay && (payments && payments.length ? (
          <div className="gtlx-paylist">
            {payments.map((p) => (
              <div key={p.id} className="gtlx-payrow">
                <span className="gtlx-pay-who">{p.full_name || (p.candidate_id ? 'Candidate ' + p.candidate_id : 'Erased candidate')}</span>
                <span className="gtlx-pay-amt">{money(p.currency, p.amount_minor)}{p.refunded_minor ? <em> ({money(p.currency, p.refunded_minor)} refunded)</em> : null}</span>
                <span className={'gtlx-paystat gtlx-paystat--' + p.status}>{PAY[p.status] || p.status}</span>
                <span className="gtlx-pay-when">{ukDate(p.paid_at || p.created_at)}</span>
                <code className="gtlx-code" title={p.stripe_session_id}>{p.stripe_payment_intent || p.stripe_session_id}</code>
              </div>
            ))}
          </div>
        ) : <p className="gtivl-muted">No payments yet. Refunds are made in Stripe and show here.</p>)}
      </section>

      {viewId && <SessionModal id={viewId} authFetch={authFetch} getToken={getToken} flash={flash} onClose={() => setViewId(null)} onChanged={loadSessions} />}
      {toast && <div className="gtivl-toast" role="status">{toast}</div>}
    </div>
  );
}
